// components/patient/DocumentUploadSection.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Upload as UploadIcon,
  FileText,
  Trash2,
  Eye,
  Download,
  ShieldCheck,
  AlertTriangle,
  X as CloseIcon,
  ExternalLink,
  Paperclip,
  Plus,
  Minus,
  Search as SearchIcon,
} from "lucide-react";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db, storage } from "../../firebase";

const UPLOAD_ROOT = "patient_documents";
const MAX_BYTES = 50 * 1024 * 1024;
const MAX_PER_FILE = 10 * 1024 * 1024;

const prettyBytes = (n = 0) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
};

const isImage = (ct = "") => /^image\//.test(ct);
const isPdf = (ct = "") => ct === "application/pdf";

const tagMeta = (tag) => {
  switch ((tag || "other").toLowerCase()) {
    case "lab":
      return {
        key: "lab",
        color: "#0A7C86",
        bg: "#EAF7F8",
        label: "Analiza",
      };
    case "imaging":
      return {
        key: "imaging",
        color: "#0F6FEF",
        bg: "#EEF4FF",
        label: "Imazheri",
      };
    case "referral":
      return {
        key: "referral",
        color: "#7C3AED",
        bg: "#F3E8FF",
        label: "Raport / rekomandim",
      };
    default:
      return {
        key: "other",
        color: "#047857",
        bg: "#ECFDF5",
        label: "Të tjera",
      };
  }
};

const Portal = ({ children }) => createPortal(children, document.body);

export default function DocumentUploadSection() {
  const [user, userLoading] = useAuthState(auth);

  const [docs, setDocs] = useState([]);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  const [filterTag, setFilterTag] = useState("all");

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");
  const [previewType, setPreviewType] = useState("");
  const overlayRef = useRef(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [upTag, setUpTag] = useState("lab");
  const [upDesc, setUpDesc] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [dragging, setDragging] = useState(false);

  const [noIndexMode, setNoIndexMode] = useState(false);
  const [indexLink, setIndexLink] = useState("");

  const [snack, setSnack] = useState(null);
  const [thumbs, setThumbs] = useState({});

  // subscribe to user docs
  useEffect(() => {
    if (userLoading || !user?.uid) return;
    let unsub = () => {};
    const colRef = collection(db, "userDocuments");

    const makeSub = (useOrderBy) => {
      const qRef = useOrderBy
        ? query(
            colRef,
            where("ownerId", "==", user.uid),
            orderBy("createdAt", "desc")
          )
        : query(colRef, where("ownerId", "==", user.uid));

      unsub = onSnapshot(
        qRef,
        (snap) => {
          const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          if (!useOrderBy) {
            rows.sort(
              (a, b) =>
                (b?.createdAt?.toMillis?.() ?? 0) -
                (a?.createdAt?.toMillis?.() ?? 0)
            );
          }
          setDocs(rows);
        },
        (err) => {
          const needsIndex =
            err?.code === "failed-precondition" &&
            /requires an index/i.test(err.message || "");
          if (needsIndex) {
            setNoIndexMode(true);
            const m = (err.message || "").match(
              /https:\/\/console\.firebase\.google\.com\/[^\s)]+/i
            );
            if (m?.[0]) setIndexLink(m[0]);
            makeSub(false);
          } else {
            setError("Nuk arritëm të ngarkojmë dokumentet.");
          }
        }
      );
    };

    makeSub(true);
    return () => unsub();
  }, [user?.uid, userLoading]);

  // thumbnails
  useEffect(() => {
    const loadThumbs = async () => {
      for (const d of docs) {
        if (!d?.storagePath) continue;
        if (!isImage(d?.contentType || "")) continue;
        if (thumbs[d.id]) continue;
        try {
          const url = await getDownloadURL(ref(storage, d.storagePath));
          setThumbs((t) => ({ ...t, [d.id]: url }));
        } catch {
          // ignore
        }
      }
    };
    loadThumbs();
  }, [docs, thumbs]);

  // keyboard shortcut for search
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !uploadOpen && !previewOpen) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape" && search) setSearch("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [search, uploadOpen, previewOpen]);

  // lock scroll when any modal open
  useEffect(() => {
    const anyOpen = previewOpen || uploadOpen;
    if (!anyOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [previewOpen, uploadOpen]);

  const totalUsed = useMemo(
    () => docs.reduce((s, d) => s + (d.size || 0), 0),
    [docs]
  );
  const remaining = Math.max(0, MAX_BYTES - totalUsed);
  const usedPct = Math.min(100, Math.round((totalUsed / MAX_BYTES) * 100));

  const filteredDocs = useMemo(() => {
    const v = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (filterTag !== "all") {
        const t = (d.tag || "other").toLowerCase();
        if (t !== filterTag) return false;
      }
      if (!v) return true;
      const hay = [
        d.fileName || "",
        d.tag || "",
        d.description || "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(v);
    });
  }, [docs, search, filterTag]);

  const viewDoc = async (d) => {
    try {
      if (!d?.storagePath) throw new Error("Missing storage path.");
      const url = await getDownloadURL(ref(storage, d.storagePath));
      setPreviewUrl(url);
      setPreviewName(
        d.description?.trim()
          ? d.description.trim()
          : d.fileName || "Dokument mjekësor"
      );
      setPreviewType(
        isImage(d.contentType) ? "image" : isPdf(d.contentType) ? "pdf" : ""
      );
      if (!isImage(d.contentType) && !isPdf(d.contentType)) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      setPreviewOpen(true);
    } catch {
      setError("Nuk mundëm të hapim dokumentin.");
    }
  };

  const openInNewTab = () => {
    if (previewUrl) {
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    }
  };

  const removeDoc = async (d) => {
    if (!window.confirm("Të fshihet ky dokument?")) return;
    try {
      const docSnap = await getDoc(doc(db, "userDocuments", d.id));
      const snapshotData = docSnap.exists() ? docSnap.data() : d;
      const storagePath = snapshotData?.storagePath;
      await deleteDoc(doc(db, "userDocuments", d.id));
      const timer = setTimeout(async () => {
        if (storagePath) {
          try {
            await deleteObject(ref(storage, storagePath));
          } catch {}
        }
        setSnack(null);
      }, 7000);
      setSnack({
        msg: "Dokumenti u fshi.",
        onUndo: async () => {
          clearTimeout(timer);
          try {
            await setDoc(doc(db, "userDocuments", d.id), snapshotData);
          } finally {
            setSnack(null);
          }
        },
      });
    } catch {
      setError("Fshirja dështoi.");
    }
  };

  const addFiles = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setSelectedFiles((prev) => {
      const keys = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const merged = [...prev];
      for (const f of arr) {
        const k = `${f.name}:${f.size}`;
        if (!keys.has(k)) merged.push(f);
      }
      return merged;
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  const startUpload = async () => {
    setError("");
    if (!user?.uid) return setError("Lidhu fillimisht në llogarinë tënde.");
    if (selectedFiles.length === 0)
      return setError("Zgjidh të paktën një skedar.");
    const totalNew = selectedFiles.reduce((s, f) => s + f.size, 0);
    if (totalNew > remaining)
      return setError(
        `Nuk ka hapësirë të mjaftueshme. Kërkohen ${prettyBytes(
          totalNew
        )}, ke ${prettyBytes(remaining)} të lira.`
      );

    setUploading(true);
    setUploadPct(0);
    try {
      let uploadedBytes = 0;
      const allBytes = selectedFiles.reduce((s, f) => s + f.size, 0);
      for (const file of selectedFiles) {
        const contentType = file.type || "application/octet-stream";
        const allow = isPdf(contentType) || isImage(contentType);
        if (!allow)
          throw new Error("Lejohen vetëm PDF ose imazhe (foto, skanime).");
        if (file.size > MAX_PER_FILE)
          throw new Error(
            `Maksimumi për skedar është ${prettyBytes(MAX_PER_FILE)}.`
          );

        const metaRef = await addDoc(collection(db, "userDocuments"), {
          ownerId: user.uid,
          fileName: file.name,
          size: file.size,
          contentType,
          tag: upTag,
          description: upDesc.trim() || null,
          storagePath: null,
          createdAt: serverTimestamp(),
        });

        const ext = (file.name.split(".").pop() || "").toLowerCase();
        const safeExt =
          ext && ext.length <= 10 ? ext : isPdf(contentType) ? "pdf" : "bin";
        const storagePath = `${UPLOAD_ROOT}/${user.uid}/${metaRef.id}.${safeExt}`;
        const sref = ref(storage, storagePath);

        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(sref, file, { contentType });
          task.on(
            "state_changed",
            (snap) => {
              const pct = (uploadedBytes + snap.bytesTransferred) / allBytes;
              setUploadPct(Math.floor(pct * 100));
            },
            reject,
            async () => {
              uploadedBytes += file.size;
              await updateDoc(doc(db, "userDocuments", metaRef.id), {
                storagePath,
              });
              resolve();
            }
          );
        });
      }
      setSelectedFiles([]);
      setUpDesc("");
      setUploadOpen(false);
    } catch (err) {
      setError(err?.message || "Ngarkimi dështoi.");
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const handleDownload = async (d) => {
    try {
      const url = await getDownloadURL(ref(storage, d.storagePath));
      const a = document.createElement("a");
      a.href = url;
      a.download = d.fileName || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError("Shkarkimi dështoi.");
    }
  };

  if (userLoading) {
    return (
      <div style={{ padding: 12, fontSize: 14, color: "#6B7280" }}>
        Po ngarkojmë dokumentet e tua…
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: 12, fontSize: 14, color: "#6B7280" }}>
        Hyr në llogarinë tënde për të ruajtur dhe parë dokumentet mjekësore.
      </div>
    );
  }

  return (
    <div className="doc-root">
      <style>{`
      .doc-root{
        font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","SF Pro Display",system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
        color:#111827;
        width:100%;
      }
      .doc-card{
        background:#FFFFFF;
        border-radius:16px;
        border:1px solid #E5E7EB;
        padding:14px 14px 10px;
        box-shadow:0 8px 20px rgba(15,23,42,0.06);
      }
      .doc-header{
        display:flex;
        align-items:flex-start;
        justify-content:space-between;
        gap:10px;
      }
      .doc-titleRow{
        display:flex;
        gap:8px;
        align-items:flex-start;
      }
      .doc-titleMain{
        font-size:15px;
        font-weight:700;
      }
      .doc-sub{
        font-size:12px;
        color:#6B7280;
        margin-top:2px;
        max-width:260px;
      }
      .doc-uploadBtn{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        gap:6px;
        border-radius:999px;
        padding:0 14px;
        height:32px;
        border:none;
        background:#0F6FEF;
        color:white;
        font-size:13px;
        font-weight:600;
        box-shadow:0 8px 16px rgba(37,99,235,0.35);
      }
      .doc-uploadBtn:active{
        transform:scale(0.98);
      }
      .doc-body{
        margin-top:10px;
        display:flex;
        flex-direction:column;
        gap:10px;
      }
      .doc-usageRow{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:8px;
        font-size:12px;
        color:#6B7280;
      }
      .doc-badgeUsage{
        padding:2px 8px;
        border-radius:999px;
        background:#EFF6FF;
        font-size:11px;
        color:#1D4ED8;
        display:inline-flex;
        align-items:center;
        gap:4px;
      }
      .doc-barOuter{
        width:100%;
        height:6px;
        border-radius:999px;
        background:#F3F4F6;
        overflow:hidden;
      }
      .doc-barInner{
        height:100%;
        background:linear-gradient(90deg,#0F6FEF,#22C55E);
      }
      .doc-searchRow{
        display:flex;
        flex-direction:column;
        gap:6px;
      }
      .doc-searchWrap{
        position:relative;
      }
      .doc-searchInput{
        width:100%;
        height:36px;
        border-radius:999px;
        border:1px solid #E5E7EB;
        padding:0 34px 0 34px;
        font-size:14px;
        outline:none;
        background:#F9FAFB;
      }
      .doc-searchIcon{
        position:absolute;
        left:10px;
        top:50%;
        transform:translateY(-50%);
        color:#9CA3AF;
      }
      .doc-searchClear{
        position:absolute;
        right:6px;
        top:50%;
        transform:translateY(-50%);
        width:22px;
        height:22px;
        border-radius:999px;
        border:1px solid #E5E7EB;
        background:#FFFFFF;
        display:flex;
        align-items:center;
        justify-content:center;
      }
      .doc-searchHint{
        font-size:11px;
        color:#9CA3AF;
        text-align:right;
      }
      .doc-kbd{
        font-family:ui-monospace,monospace;
        border-radius:4px;
        border:1px solid #E5E7EB;
        padding:1px 5px;
        background:#F3F4F6;
      }
      .doc-filterRow{
        display:flex;
        flex-wrap:wrap;
        gap:6px;
        margin-top:4px;
      }
      .doc-pill{
        border-radius:999px;
        border:1px solid #E5E7EB;
        padding:4px 10px;
        font-size:11px;
        display:inline-flex;
        align-items:center;
        gap:6px;
        background:#FFFFFF;
        color:#4B5563;
      }
      .doc-pillActive{
        background:#0F172A;
        color:#F9FAFB;
        border-color:#0F172A;
      }
      .doc-listHeader{
        margin-top:4px;
        font-size:12px;
        font-weight:600;
        color:#4B5563;
      }
      .doc-empty{
        font-size:13px;
        color:#6B7280;
        text-align:center;
        padding:18px 10px 6px;
      }
      .doc-item{
        display:flex;
        align-items:center;
        gap:10px;
        padding:10px 4px;
        border-bottom:1px solid #E5E7EB;
      }
      .doc-thumb{
        width:40px;
        height:40px;
        border-radius:12px;
        background:#F3F4F6;
        display:flex;
        align-items:center;
        justify-content:center;
        overflow:hidden;
      }
      .doc-thumb img{
        width:100%;
        height:100%;
        object-fit:cover;
      }
      .doc-main{
        flex:1;
        min-width:0;
      }
      .doc-mainTitle{
        font-size:14px;
        font-weight:600;
        color:#111827;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }
      .doc-mainSub{
        font-size:11px;
        color:#6B7280;
        margin-top:2px;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }
      .doc-metaRow{
        display:flex;
        flex-wrap:wrap;
        gap:6px;
        margin-top:4px;
        font-size:11px;
        align-items:center;
      }
      .doc-tagPill{
        border-radius:999px;
        padding:2px 8px;
        display:inline-flex;
        align-items:center;
        gap:4px;
        font-size:11px;
      }
      .doc-actions{
        display:flex;
        flex-direction:column;
        gap:4px;
      }
      .doc-iconBtn{
        width:30px;
        height:30px;
        border-radius:999px;
        border:1px solid #E5E7EB;
        background:#FFFFFF;
        display:flex;
        align-items:center;
        justify-content:center;
      }
      .doc-iconBtnPrimary{
        border-color:#0F6FEF;
        color:#0F6FEF;
        background:#EFF6FF;
      }
      .doc-iconBtnDanger{
        border-color:#FCA5A5;
        color:#B91C1C;
        background:#FEF2F2;
      }
      .doc-banner{
        margin-top:8px;
        border-radius:10px;
        border:1px dashed #FBBF24;
        background:#FFFBEB;
        padding:8px 10px;
        font-size:12px;
        color:#92400E;
        display:flex;
        align-items:flex-start;
        gap:6px;
      }
      .doc-snack{
        position:fixed;
        left:50%;
        transform:translateX(-50%);
        bottom:12px;
        z-index:1100;
        background:#111827;
        color:white;
        border-radius:999px;
        padding:8px 12px;
        display:flex;
        align-items:center;
        gap:10px;
        font-size:13px;
      }
      .doc-snack button{
        border:none;
        border-radius:999px;
        padding:4px 10px;
        background:#22C55E;
        color:#052E16;
        font-weight:600;
      }
      .doc-overlay{
        position:fixed;
        inset:0;
        background:rgba(15,23,42,0.45);
        backdrop-filter:blur(8px);
        display:flex;
        align-items:center;
        justify-content:center;
        padding:10px;
        z-index:1000;
      }
      .doc-modal{
        width:100%;
        max-width:420px;
        background:white;
        border-radius:18px;
        box-shadow:0 24px 60px rgba(15,23,42,0.35);
        display:flex;
        flex-direction:column;
        max-height:90vh;
      }
      .doc-modalHeader{
        padding:10px 14px;
        border-bottom:1px solid #E5E7EB;
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:8px;
      }
      .doc-modalBody{
        padding:12px 14px;
        overflow:auto;
      }
      .doc-modalFooter{
        padding:10px 14px;
        border-top:1px solid #E5E7EB;
        display:flex;
        justify-content:flex-end;
        gap:8px;
      }
      .doc-fieldLabel{
        font-size:12px;
        font-weight:600;
        color:#374151;
        margin-bottom:4px;
      }
      .doc-input,
      .doc-textarea,
      .doc-select{
        width:100%;
        border-radius:12px;
        border:1px solid #E5E7EB;
        padding:8px 10px;
        font-size:13px;
        background:#F9FAFB;
      }
      .doc-textarea{
        resize:vertical;
      }
      .doc-drop{
        margin-top:10px;
        border-radius:14px;
        border:1px dashed #CBD5F5;
        padding:12px 10px;
        text-align:center;
        background:#F9FAFF;
      }
      .doc-drop.drag{
        border-color:#0F6FEF;
        background:#EEF2FF;
      }
      .doc-dropTitle{
        font-size:13px;
        font-weight:600;
        margin-top:4px;
      }
      .doc-dropSub{
        font-size:11px;
        color:#6B7280;
        margin-top:2px;
      }
      .doc-chooseBtn{
        margin-top:8px;
        border-radius:999px;
        border:none;
        padding:6px 14px;
        font-size:13px;
        font-weight:600;
        background:#0F172A;
        color:white;
        display:inline-flex;
        align-items:center;
        gap:6px;
      }
      .doc-fileChip{
        margin-top:10px;
        border-radius:12px;
        border:1px solid #E5E7EB;
        padding:8px 10px;
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:8px;
        font-size:12px;
      }
      .doc-btn{
        border-radius:999px;
        border:1px solid #E5E7EB;
        padding:6px 12px;
        font-size:13px;
        background:white;
        display:inline-flex;
        align-items:center;
        gap:6px;
      }
      .doc-btnPrimary{
        background:#0F6FEF;
        border-color:#0F6FEF;
        color:white;
      }
      .doc-progressLine{
        margin-top:8px;
        width:100%;
        height:6px;
        border-radius:999px;
        background:#E5E7EB;
        overflow:hidden;
      }
      .doc-progressInner{
        height:100%;
        background:#0EA5E9;
      }
      `}</style>

      <div className="doc-card" role="region" aria-label="Dokumentet e tua">
        {/* HEADER */}
        <div className="doc-header">
          <div className="doc-titleRow">
            <ShieldCheck size={18} color="#0F766E" />
            <div>
              <div className="doc-titleMain">Dokumentet e mia</div>
              <div className="doc-sub">
                Ruaj analizat, epikrizat dhe përgjigjet e ekzaminimeve në një
                vend të sigurt, vetëm për ty.
              </div>
            </div>
          </div>
          <button
            className="doc-uploadBtn"
            onClick={() => setUploadOpen(true)}
          >
            <UploadIcon size={16} /> Ngarko
          </button>
        </div>

        {/* BODY */}
        <div className="doc-body">
          {/* usage */}
          <div className="doc-usageRow">
            <span>
              Po përdor{" "}
              <strong>{prettyBytes(totalUsed)}</strong> nga{" "}
              {prettyBytes(MAX_BYTES)}.
            </span>
            <span className="doc-badgeUsage">
              {usedPct}% e hapësirës
            </span>
          </div>
          <div className="doc-barOuter" aria-hidden>
            <div
              className="doc-barInner"
              style={{ width: `${usedPct}%` }}
            />
          </div>

          {/* search + filters */}
          <div className="doc-searchRow">
            <div className="doc-searchWrap">
              <SearchIcon
                className="doc-searchIcon"
                size={16}
                aria-hidden
              />
              <input
                ref={searchRef}
                className="doc-searchInput"
                type="search"
                placeholder="Kërko sipas emrit, etiketës ose shënimeve…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Kërko dokumentet"
              />
              {search && (
                <button
                  className="doc-searchClear"
                  onClick={() => setSearch("")}
                  aria-label="Fshi kërkimin"
                >
                  <CloseIcon size={14} />
                </button>
              )}
            </div>
            <div className="doc-searchHint">
              Shkruaj ose shtyp <span className="doc-kbd">/</span> për të
              kërkuar shpejt.
            </div>
          </div>

          {/* tag filters */}
          <div className="doc-filterRow">
            {["all", "lab", "imaging", "referral", "other"].map((key) => {
              const meta =
                key === "all"
                  ? {
                      label: "Të gjitha",
                    }
                  : tagMeta(key);
              const active = filterTag === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={
                    "doc-pill" + (active ? " doc-pillActive" : "")
                  }
                  onClick={() => setFilterTag(key)}
                >
                  {key === "all" ? (
                    <FileText size={14} />
                  ) : (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background:
                          key === "lab"
                            ? "#0A7C86"
                            : key === "imaging"
                            ? "#0F6FEF"
                            : key === "referral"
                            ? "#7C3AED"
                            : "#047857",
                      }}
                    />
                  )}
                  <span>{meta.label}</span>
                </button>
              );
            })}
          </div>

          <div className="doc-listHeader">
            Dokumentet e ruajtura ({filteredDocs.length})
          </div>

          {/* index warning */}
          {noIndexMode && (
            <div className="doc-banner">
              <AlertTriangle size={16} />
              <div>
                Po shfaqim dokumentet pa renditje nga serveri.{" "}
                {indexLink ? (
                  <a href={indexLink} target="_blank" rel="noreferrer">
                    Krijo indeksin në Firebase për performancë më të mirë.
                  </a>
                ) : (
                  "Krijo indeksin e përbërë në Firebase Console për renditje më të shpejtë."
                )}
              </div>
            </div>
          )}

          {/* list */}
          {filteredDocs.length === 0 ? (
            <div className="doc-empty">
              {search ? (
                <>
                  Nuk u gjet asnjë dokument për &ldquo;
                  <strong>{search}</strong>&rdquo;.
                </>
              ) : (
                <>Ende nuk ke ngarkuar dokumente. Shto PDF ose foto nga butoni &ldquo;Ngarko&rdquo;.</>
              )}
            </div>
          ) : (
            filteredDocs.map((d) => {
              const meta = tagMeta(d.tag);
              const thumb = isImage(d.contentType) ? thumbs[d.id] : null;
              const created =
                d.createdAt?.toDate &&
                d.createdAt.toDate().toLocaleDateString("sq-AL");
              return (
                <div key={d.id} className="doc-item">
                  <div className="doc-thumb" onClick={() => viewDoc(d)}>
                    {thumb ? (
                      <img src={thumb} alt="" />
                    ) : (
                      <FileText size={18} color="#4B5563" />
                    )}
                  </div>

                  <div className="doc-main" onClick={() => viewDoc(d)}>
                    <div className="doc-mainTitle">
                      {d.description?.trim()
                        ? d.description.trim()
                        : thumb
                        ? "Imazh mjekësor"
                        : "Dokument mjekësor"}
                    </div>
                    {d.fileName && (
                      <div className="doc-mainSub">{d.fileName}</div>
                    )}
                    <div className="doc-metaRow">
                      <span
                        className="doc-tagPill"
                        style={{
                          background: meta.bg,
                          color: meta.color,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: 999,
                            background: meta.color,
                          }}
                        />
                        {meta.label}
                      </span>
                      <span>{prettyBytes(d.size)}</span>
                      {created && <span>• {created}</span>}
                    </div>
                  </div>

                  <div className="doc-actions">
                    <button
                      className="doc-iconBtn doc-iconBtnPrimary"
                      onClick={() => viewDoc(d)}
                      aria-label="Shiko"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="doc-iconBtn"
                      onClick={() => handleDownload(d)}
                      aria-label="Shkarko"
                    >
                      <Download size={16} />
                    </button>
                    <button
                      className="doc-iconBtn doc-iconBtnDanger"
                      onClick={() => removeDoc(d)}
                      aria-label="Fshi"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {error && (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "#B91C1C",
                background: "#FEF2F2",
                borderRadius: 10,
                border: "1px solid #FECACA",
                padding: "8px 10px",
              }}
            >
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {uploadOpen && (
        <Portal>
          <div
            className="doc-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Ngarko dokumente"
            onClick={(e) => {
              if (e.target === e.currentTarget && !uploading) {
                setUploadOpen(false);
              }
            }}
          >
            <div className="doc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="doc-modalHeader">
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  Ngarko dokumente
                </div>
                <button
                  className="doc-iconBtn"
                  onClick={() => !uploading && setUploadOpen(false)}
                  aria-label="Mbyll"
                >
                  <CloseIcon size={16} />
                </button>
              </div>
              <div className="doc-modalBody">
                <div>
                  <div className="doc-fieldLabel">Lloji i dokumentit</div>
                  <select
                    className="doc-select"
                    value={upTag}
                    onChange={(e) => setUpTag(e.target.value)}
                  >
                    <option value="lab">Analiza laboratorike</option>
                    <option value="imaging">Imazheri (CT, MRI, eko…)</option>
                    <option value="referral">Raport / rekomandim</option>
                    <option value="other">Të tjera</option>
                  </select>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div className="doc-fieldLabel">Përshkrim (opsional)</div>
                  <textarea
                    className="doc-textarea"
                    rows={2}
                    value={upDesc}
                    onChange={(e) => setUpDesc(e.target.value)}
                    placeholder="Shkruaj shkurt për çfarë është ky dokument (p.sh. Analiza gjaku janar 2025)…"
                  />
                </div>

                <div
                  className={
                    "doc-drop" + (dragging ? " drag" : "")
                  }
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                >
                  <Paperclip size={18} />
                  <div className="doc-dropTitle">
                    Tërhiq dhe lësho PDF ose foto këtu
                  </div>
                  <div className="doc-dropSub">
                    Maks {prettyBytes(MAX_PER_FILE)} për skedar
                  </div>
                 <div style={{ marginTop: 8 }}>
  <button
    type="button"
    className="doc-chooseBtn"
    onClick={() => document.getElementById("filePicker").click()}
  >
    <Plus size={16} />
    Zgjidh skedarë
  </button>

  <input
    id="filePicker"
    type="file"
    accept="application/pdf,image/*"
    multiple
    onChange={(e) => addFiles(e.target.files)}
    style={{ display: "none" }}
  />
</div>

                </div>

                {selectedFiles.length > 0 && (
                  <div>
                    {selectedFiles.map((f, i) => (
                      <div key={i} className="doc-fileChip">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            minWidth: 0,
                          }}
                        >
                          <div className="doc-thumb">
                            {isImage(f.type)
                              ? "IMG"
                              : isPdf(f.type)
                              ? "PDF"
                              : "FILE"}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 240,
                              }}
                            >
                              {f.name}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: "#6B7280",
                              }}
                            >
                              {prettyBytes(f.size)}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="doc-btn"
                          onClick={() =>
                            setSelectedFiles((prev) =>
                              prev.filter((_, idx) => idx !== i)
                            )
                          }
                        >
                          <Minus size={14} /> Hiq
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {uploading && (
                  <div>
                    <div className="doc-progressLine">
                      <div
                        className="doc-progressInner"
                        style={{ width: `${uploadPct}%` }}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 12,
                        color: "#6B7280",
                        textAlign: "right",
                      }}
                    >
                      {uploadPct}%
                    </div>
                  </div>
                )}
              </div>
              <div className="doc-modalFooter">
                <button
                  className="doc-btn"
                  onClick={() => !uploading && setUploadOpen(false)}
                  disabled={uploading}
                >
                  Anulo
                </button>
                <button
                  className="doc-btn doc-btnPrimary"
                  onClick={startUpload}
                  disabled={uploading || selectedFiles.length === 0}
                >
                  <UploadIcon size={16} />
                  {uploading ? "Po ngarkohet…" : "Ngarko"}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Preview Modal */}
      {previewOpen && (
        <Portal>
          <div
            className="doc-overlay"
            ref={overlayRef}
            onClick={(e) => {
              if (e.target === overlayRef.current) setPreviewOpen(false);
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Parapamje dokumenti"
          >
            <div className="doc-modal" style={{ maxWidth: 520 }}>
              <div className="doc-modalHeader">
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {previewName}
                </div>
                <button
                  className="doc-iconBtn"
                  onClick={() => setPreviewOpen(false)}
                  aria-label="Mbyll parapamjen"
                >
                  <CloseIcon size={16} />
                </button>
              </div>
              <div
                className="doc-modalBody"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {previewType === "image" ? (
                  <img
                    src={previewUrl}
                    alt={previewName || "image"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      borderRadius: 12,
                    }}
                  />
                ) : previewType === "pdf" ? (
                  <iframe
                    title={previewName || "PDF"}
                    src={previewUrl}
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "none",
                      borderRadius: 12,
                    }}
                  />
                ) : null}
              </div>
              <div className="doc-modalFooter">
                <button className="doc-btn" onClick={openInNewTab}>
                  <ExternalLink size={16} /> Hap në dritare të re
                </button>
                <button
                  className="doc-btn"
                  onClick={() => setPreviewOpen(false)}
                >
                  Mbyll
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Snackbar */}
      {snack && (
        <div className="doc-snack" role="status" aria-live="polite">
          <span>{snack.msg}</span>
          <button onClick={snack.onUndo}>Anulo</button>
        </div>
      )}
    </div>
  );
}
